import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, ShieldOff, Eye, Target, Clock, Check, HelpCircle, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { getToken } from '../lib/auth';
import { badgeUrl } from '../lib/assetUrls';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
    return { Authorization: `Bearer ${getToken()}` };
}

function Crest({ teamId, teamName, size = 34 }) {
    const [err, setErr] = useState(false);
    if (err) {
        return (
            <div
                className="rounded-full bg-muted flex items-center justify-center flex-shrink-0"
                style={{ width: size, height: size }}
            >
                <span className="text-[10px] font-bold">{(teamName || '?')[0]}</span>
            </div>
        );
    }
    return (
        <img
            src={badgeUrl(teamId)}
            alt={teamName}
            className="object-contain flex-shrink-0"
            style={{ width: size, height: size }}
            onError={() => setErr(true)}
        />
    );
}

function initials(name) {
    return (name || '?')
        .split(' ')
        .map(w => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
}

function formatLockCountdown(lockTime) {
    if (!lockTime) return null;
    const diffMs = new Date(lockTime).getTime() - Date.now();
    if (diffMs <= 0) return 'Locked';
    const hrs = Math.floor(diffMs / 3_600_000);
    const mins = Math.floor((diffMs % 3_600_000) / 60_000);
    if (hrs >= 24) {
        const days = Math.floor(hrs / 24);
        return `Picks lock in ${days}d ${hrs % 24}h`;
    }
    if (hrs > 0) return `Picks lock in ${hrs}h ${mins}m`;
    return `Picks lock in ${mins}m`;
}

// ── One fixture: two clickable halves (home / away) ──────────────────────────
function FixtureBox({ match, myPick, usedTeamIds, locked, isEliminated, onPick }) {
    const isLive = match.status === 'live';
    const isFinished = match.status === 'finished';
    const homeUsed = usedTeamIds.has(String(match.home_team_id));
    const awayUsed = usedTeamIds.has(String(match.away_team_id));
    const pickedSide = myPick?.pa_match_id === match.pa_match_id ? myPick.side : null;

    const canClick = (side, used) =>
        !isEliminated && !locked && !used && !myPick && !isLive && !isFinished
            ? () => onPick(match, side)
            : undefined;

    const winProb = match.win_probability; // {home_pct, draw_pct, away_pct} or null/undefined
    const favouriteSide = winProb
        ? (winProb.home_pct >= winProb.away_pct ? 'home' : 'away')
        : null;

    const Side = ({ side, teamId, teamName, used }) => {
        const clickable = canClick(side, used);
        const picked = pickedSide === side;
        return (
            <div
                onClick={clickable}
                className={`flex-1 flex flex-col items-center gap-1.5 py-2.5 px-1.5 relative
                    ${clickable ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'}
                    ${picked ? 'bg-primary/10' : ''} ${used && !picked ? 'opacity-40' : ''}
                    transition-colors`}
                style={picked ? { boxShadow: 'inset 0 0 0 1.5px hsl(var(--primary))' } : undefined}
            >
                <Crest teamId={teamId} teamName={teamName} />
                <span className={`text-[11px] font-semibold text-center leading-tight ${picked ? 'text-primary' : ''}`}>
                    {teamName}
                </span>
                {picked && (
                    <span className="text-[8.5px] font-bold uppercase tracking-wide text-primary bg-primary/15 rounded px-1">
                        Your pick
                    </span>
                )}
                {used && !picked && (
                    <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-black/50 flex items-center justify-center">
                        <Lock className="h-2.5 w-2.5 text-white" />
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="rounded-xl border bg-card overflow-hidden flex flex-col">
            {isLive ? (
                <div className="flex items-center justify-center gap-1.5 py-1.5 border-b bg-muted/30">
                    <Badge className="text-[9px] px-1.5 py-0 bg-green-500 text-white">LIVE</Badge>
                    <span className="text-sm font-bold tabular-nums">{match.home_score}–{match.away_score}</span>
                </div>
            ) : (
                <p className="text-[10px] text-center text-muted-foreground py-2 border-b font-medium">
                    {match.kickoff_time ? new Date(match.kickoff_time).toLocaleString('en-GB', {
                        weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                    }) : 'TBC'}
                </p>
            )}
            <div className="flex flex-1 divide-x">
                <Side side="home" teamId={match.home_team_id} teamName={match.home_team_name} used={homeUsed} />
                <Side side="away" teamId={match.away_team_id} teamName={match.away_team_name} used={awayUsed} />
            </div>
            {isFinished && (
                <div className="border-t py-2.5 flex flex-col items-center gap-0.5 bg-muted/30">
                    <span className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground">Full Time</span>
                    <span className="text-2xl font-bold tabular-nums font-data">{match.home_score}–{match.away_score}</span>
                </div>
            )}
            {winProb && (
                <div className="border-t" title="Win probability, implied from bookmaker odds (margin removed)">
                    <div className="text-center text-[9px] font-extrabold uppercase tracking-wide py-1 bg-[hsl(var(--podium-turquoise))] text-[hsl(var(--podium-blue))]">
                        Win probability
                    </div>
                    <div className="flex divide-x">
                        <div className="flex-1 text-center py-1.5">
                            <span className={`text-xs font-bold tabular-nums ${favouriteSide === 'home' ? 'text-green-500' : 'text-muted-foreground'}`}>
                                {winProb.home_pct}%
                            </span>
                        </div>
                        <div className="flex-1 text-center py-1.5">
                            <span className={`text-xs font-bold tabular-nums ${favouriteSide === 'away' ? 'text-green-500' : 'text-muted-foreground'}`}>
                                {winProb.away_pct}%
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Live standings sidebar: who's picked this gameweek + alive/out status ───
function LiveStandingsSidebar({ standings, loading, round, currentUserId }) {
    const list = standings || [];
    const outCount = list.filter(s => s.eliminated).length;

    return (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-bold">Standings</CardTitle>
                    <span className="text-xs text-muted-foreground font-medium">
                        {list.length} players · {outCount} out
                    </span>
                </div>
            </CardHeader>
            <CardContent className="p-0">
                {loading ? (
                    <div className="p-4"><Skeleton className="h-40 w-full" /></div>
                ) : list.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No entrants yet.</p>
                ) : (
                    list.map(s => {
                        const pick = round ? s.history.find(h => h.round_number === round.round_number) : null;
                        return (
                            <div
                                key={s.user_id}
                                className={`flex items-center gap-2.5 px-4 py-2.5 border-b last:border-0
                                    ${s.eliminated ? 'opacity-50' : ''} ${s.user_id === currentUserId ? 'bg-primary/5' : ''}`}
                            >
                                <Avatar className="h-7 w-7 flex-shrink-0">
                                    <AvatarFallback className="text-[10px]">{initials(s.username)}</AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold truncate">{s.username}</p>
                                    <span className={`text-[9px] font-bold uppercase tracking-wide ${s.eliminated ? 'text-destructive/70' : 'text-green-500'}`}>
                                        {s.eliminated ? 'Out' : 'Alive'}
                                    </span>
                                </div>
                                {pick?.revealed ? (
                                    <div
                                        title={`${pick.team_name}${pick.result ? ` — ${pick.result}` : ''}`}
                                        className={`flex-shrink-0 rounded-full ${pick.result === 'won' ? 'ring-2 ring-green-500 bg-green-500/10 p-0.5' : ''}`}
                                    >
                                        <Crest teamId={pick.team_id} teamName={pick.team_name} size={22} />
                                    </div>
                                ) : pick ? (
                                    <div
                                        title="Picked, hidden until this gameweek locks"
                                        className="w-[22px] h-[22px] rounded-full bg-green-500/15 border border-green-500/40 flex items-center justify-center flex-shrink-0"
                                    >
                                        <Check className="h-3 w-3 text-green-500" strokeWidth={3} />
                                    </div>
                                ) : (
                                    <span className="text-[9px] text-muted-foreground italic flex-shrink-0">No pick</span>
                                )}
                            </div>
                        );
                    })
                )}
            </CardContent>
        </Card>
    );
}

// ── Standings: every entrant + their pick history, no scoring/ranking ────────
function StandingsList({ standings, currentUserId }) {
    if (!standings?.length) {
        return <p className="text-sm text-muted-foreground text-center py-8">No entrants yet.</p>;
    }
    return (
        <Card>
            <CardContent className="p-0">
                {standings.map(s => (
                    <div
                        key={s.user_id}
                        className={`flex items-center gap-3 px-4 py-3 border-b last:border-0
                            ${s.eliminated ? 'opacity-50' : ''} ${s.user_id === currentUserId ? 'bg-primary/5' : ''}`}
                    >
                        <Avatar className="h-8 w-8 flex-shrink-0">
                            <AvatarFallback className="text-xs">{initials(s.username)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-shrink-0 min-w-[150px]">
                            <p className="text-sm font-medium truncate flex items-center gap-1.5">
                                {s.username}
                                {s.eliminated ? (
                                    <span className="text-[9px] font-semibold text-destructive/70 bg-destructive/10 px-1.5 py-0.5 rounded">
                                        OUT{s.eliminated_round_number ? ` · GW${s.eliminated_round_number}` : ''}
                                    </span>
                                ) : (
                                    <span className="text-[9px] font-semibold text-green-500 bg-green-500/10 px-1.5 py-0.5 rounded">
                                        ALIVE
                                    </span>
                                )}
                            </p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap flex-1">
                            {s.history.map((h, i) => {
                                if (!h.revealed) {
                                    return (
                                        <div
                                            key={h.round_id}
                                            title={`GW${h.round_number}: Picked, hidden until this gameweek locks`}
                                            className="flex flex-col items-center gap-0.5 w-11 py-1 rounded-lg border bg-green-500/10 border-green-500/40 flex-shrink-0"
                                        >
                                            <span className="text-[8px] font-bold text-muted-foreground uppercase leading-none">GW{h.round_number}</span>
                                            <Check className="h-4 w-4 text-green-500" strokeWidth={3} />
                                            <span className="text-[8px] font-semibold text-green-500 leading-none">Picked</span>
                                        </div>
                                    );
                                }
                                if (h.voided) {
                                    return (
                                        <div
                                            key={h.round_id}
                                            title={`GW${h.round_number}: ${h.team_name} (voided)`}
                                            className="flex flex-col items-center gap-0.5 w-11 py-1 rounded-lg border bg-muted/30 border-muted opacity-50 flex-shrink-0"
                                        >
                                            <span className="text-[8px] font-bold text-muted-foreground uppercase leading-none">GW{h.round_number}</span>
                                            <Crest teamId={h.team_id} teamName={h.team_name} size={20} />
                                            <span className="text-[8px] text-muted-foreground leading-none">Voided</span>
                                        </div>
                                    );
                                }
                                const outcomeStyle = {
                                    won:   { box: 'bg-green-500/10 border-green-500/40', text: 'text-green-500', label: '' },
                                    drawn: { box: 'bg-amber-500/10 border-amber-500/40', text: 'text-amber-500', label: 'Drew ' },
                                    lost:  { box: 'bg-red-500/10 border-red-500/40',     text: 'text-red-500',   label: 'Lost ' },
                                }[h.result] || { box: 'bg-muted/30 border-muted', text: 'text-muted-foreground', label: '' };
                                return (
                                    <div
                                        key={h.round_id}
                                        title={`GW${h.round_number}: ${h.team_name}${h.result ? `, ${h.result}${h.score ? ` ${h.score}` : ''}` : ''}`}
                                        className={`flex flex-col items-center gap-0.5 w-11 py-1 rounded-lg border flex-shrink-0 ${outcomeStyle.box}`}
                                    >
                                        <span className="text-[8px] font-bold text-muted-foreground uppercase leading-none">GW{h.round_number}</span>
                                        <Crest teamId={h.team_id} teamName={h.team_name} size={20} />
                                        <span className={`text-[9px] font-bold leading-none tabular-nums ${outcomeStyle.text}`}>
                                            {h.score ? `${outcomeStyle.label}${h.score}` : 'TBD'}
                                        </span>
                                    </div>
                                );
                            })}
                            {s.history.length === 0 && (
                                <span className="text-xs text-muted-foreground italic">No picks yet</span>
                            )}
                        </div>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}

// ── How to Play — collapsible rules explainer, closed by default ─────────────
function HowToPlayLMS() {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <Card className="mb-4 border-blue-500/30 bg-blue-500/5">
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-blue-500/10 transition-colors py-3">
                        <CardTitle className="flex items-center justify-between text-base">
                            <div className="flex items-center gap-2">
                                <HelpCircle className="h-5 w-5 text-blue-500" />
                                <span>How to Play</span>
                            </div>
                            <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </CardTitle>
                    </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <CardContent className="pt-0 space-y-4">
                        <div className="p-3 bg-gradient-to-r from-green-500/20 to-green-500/5 rounded-lg border border-green-500/30">
                            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <span className="text-lg">🎯</span>
                                The Simple Version
                            </h4>
                            <p className="text-sm text-muted-foreground">
                                Each gameweek, pick <strong className="text-green-500">one team to win</strong> their match.
                                Get it right and you survive to the next gameweek. Get it wrong and you're out.
                                Last player standing wins.
                            </p>
                        </div>

                        <div className="p-3 bg-muted/50 rounded-lg">
                            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <Target className="h-4 w-4 text-primary" />
                                The Rules
                            </h4>
                            <ul className="text-sm text-muted-foreground space-y-2">
                                <li>• <strong>Win = survive.</strong> Draw or lose and you're eliminated for the rest of the competition.</li>
                                <li>• <strong>One team, one use.</strong> Once you've picked a team, you can't pick it again. Once every team in the pool has been used, the pool resets and they all become available again.</li>
                                <li>• <strong>Once you lock in a pick, that's final.</strong> You can't change it. The whole gameweek locks together, 30 minutes before its earliest kickoff.</li>
                                <li>• <strong>Other picks stay hidden.</strong> You'll see a checkmark for anyone who's picked, but not which team, so nobody can copy or counter-pick. Everyone's picks reveal once the gameweek locks.</li>
                                <li>• <strong>Miss the deadline? You're not automatically out.</strong> If you don't pick in time, you're given the highest-placed team (by league position) from that gameweek's fixtures that you haven't already used. You stay in the game.</li>
                                <li>• <strong>Last one standing wins.</strong> If everyone gets eliminated in the same gameweek, the competition ends with no winner.</li>
                            </ul>
                        </div>
                    </CardContent>
                </CollapsibleContent>
            </Collapsible>
        </Card>
    );
}

export default function LastManStandingCompetitionPage({ compId, comp, isEliminated }) {
    const [activeView, setActiveView] = useState('gameweek'); // 'gameweek' | 'standings'
    const [gameweek, setGameweek] = useState(null);
    const [loading, setLoading] = useState(true);
    const [standings, setStandings] = useState(null);
    const [standingsLoading, setStandingsLoading] = useState(false);
    const [confirmPick, setConfirmPick] = useState(null); // { match, side, teamId, teamName }
    const [submitting, setSubmitting] = useState(false);
    const [now, setNow] = useState(Date.now());

    const fetchGameweek = useCallback(async () => {
        try {
            const res = await axios.get(
                `${API_URL}/api/football/lms/competitions/${compId}/gameweek`,
                { headers: authHeaders() }
            );
            setGameweek(res.data);
        } catch {
            toast.error('Failed to load this gameweek');
        } finally {
            setLoading(false);
        }
    }, [compId]);

    const fetchStandings = useCallback(async () => {
        setStandingsLoading(true);
        try {
            const res = await axios.get(
                `${API_URL}/api/football/lms/competitions/${compId}/standings`,
                { headers: authHeaders() }
            );
            setStandings(res.data.standings || []);
        } catch {
            toast.error('Failed to load standings');
        } finally {
            setStandingsLoading(false);
        }
    }, [compId]);

    useEffect(() => { fetchGameweek(); fetchStandings(); }, [fetchGameweek, fetchStandings]);

    // Live tick for the lock countdown + periodic refresh so live scores/standings update
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30000);
        const refresh = setInterval(() => { fetchGameweek(); fetchStandings(); }, 60000);
        return () => { clearInterval(t); clearInterval(refresh); };
    }, [fetchGameweek, fetchStandings]);

    const usedTeamIds = useMemo(
        () => new Set((gameweek?.used_team_ids || []).map(String)),
        [gameweek]
    );

    const handlePickClick = (match, side) => {
        const teamId = side === 'home' ? match.home_team_id : match.away_team_id;
        const teamName = side === 'home' ? match.home_team_name : match.away_team_name;
        setConfirmPick({ match, side, teamId, teamName });
    };

    const confirmSubmit = async () => {
        if (!confirmPick) return;
        setSubmitting(true);
        try {
            await axios.post(
                `${API_URL}/api/football/lms/competitions/${compId}/rounds/${gameweek.round.id}/pick`,
                { pa_match_id: confirmPick.match.pa_match_id, team_id: String(confirmPick.teamId) },
                { headers: authHeaders() }
            );
            toast.success(`${confirmPick.teamName} locked in for Gameweek ${gameweek.round.round_number}!`);
            setConfirmPick(null);
            fetchGameweek();
            fetchStandings();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to lock in your pick');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="container-app py-8">
                <Skeleton className="h-10 w-1/3 mb-2" />
                <Skeleton className="h-5 w-1/4 mb-8" />
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {[...Array(10)].map((_, i) => <Skeleton key={i} className="h-32" />)}
                </div>
            </div>
        );
    }

    const round = gameweek?.round;
    const matches = gameweek?.matches || [];
    const lockLabel = round ? formatLockCountdown(gameweek.lock_time) : null;

    return (
        <div className="container-app py-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                <div className="flex items-start gap-3">
                    <Link to="/football">
                        <Button variant="ghost" size="icon" className="mt-0.5">
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                    </Link>
                    <div>
                        <h1 className="text-3xl font-bold font-heading page-title">{comp?.name}</h1>
                        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-1">
                            Last Man Standing
                        </p>
                    </div>
                </div>
                {round && (
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-sm font-semibold px-3 py-1.5">
                            Gameweek {round.round_number}
                        </Badge>
                        {!round.voided && lockLabel && (
                            <Badge
                                variant="outline"
                                className={`text-xs px-3 py-1.5 gap-1.5 ${lockLabel === 'Locked' ? 'border-muted-foreground/40' : 'border-amber-500/40 text-amber-500'}`}
                            >
                                <Clock className="h-3 w-3" />
                                {lockLabel}
                            </Badge>
                        )}
                        {round.voided && (
                            <Badge variant="outline" className="text-xs px-3 py-1.5 border-destructive/40 text-destructive">
                                Gameweek voided{round.void_reason ? `: ${round.void_reason}` : ''}
                            </Badge>
                        )}
                    </div>
                )}
            </div>

            <HowToPlayLMS />

            {isEliminated && (
                <Card className="mb-6 border-destructive/40 bg-destructive/5">
                    <CardContent className="py-4 flex items-center gap-3">
                        <ShieldOff className="h-5 w-5 text-destructive flex-shrink-0" />
                        <div>
                            <p className="font-semibold text-destructive">You've been eliminated</p>
                            <p className="text-sm text-muted-foreground">
                                You're now spectating. Follow the remaining gameweeks and standings, but picks are closed for you.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="flex flex-col lg:flex-row gap-6 items-start">
                <div className="flex-1 min-w-0 w-full">
                    {/* View toggle */}
                    <div className="flex gap-2 mb-4">
                        <button
                            onClick={() => setActiveView('gameweek')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                activeView === 'gameweek' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            <Target className="h-3.5 w-3.5" />
                            This Gameweek
                        </button>
                        <button
                            onClick={() => setActiveView('standings')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                activeView === 'standings' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            <Eye className="h-3.5 w-3.5" />
                            Standings
                        </button>
                    </div>

                    {activeView === 'gameweek' ? (
                        !round ? (
                            <Card>
                                <CardContent className="py-10 text-center text-muted-foreground">
                                    No gameweek is open yet. Check back once the admin sets one up.
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                                {matches.map(match => (
                                    <FixtureBox
                                        key={match.pa_match_id}
                                        match={match}
                                        myPick={gameweek.my_pick}
                                        usedTeamIds={usedTeamIds}
                                        locked={gameweek.locked || round.voided}
                                        isEliminated={isEliminated}
                                        onPick={handlePickClick}
                                    />
                                ))}
                            </div>
                        )
                    ) : (
                        standingsLoading ? (
                            <Skeleton className="h-64 w-full" />
                        ) : (
                            <StandingsList standings={standings} currentUserId={comp?.current_user_id} />
                        )
                    )}
                </div>

                {activeView === 'gameweek' && (
                    <aside className="w-full lg:w-80 flex-shrink-0">
                        <LiveStandingsSidebar
                            standings={standings}
                            loading={standingsLoading}
                            round={round}
                            currentUserId={comp?.current_user_id}
                        />
                    </aside>
                )}
            </div>

            {/* Confirm pick dialog */}
            <Dialog open={!!confirmPick} onOpenChange={(open) => !open && setConfirmPick(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Lock className="h-4 w-4 text-primary" />
                            Confirm your pick
                        </DialogTitle>
                        <DialogDescription>
                            {confirmPick && (
                                <>
                                    <strong>{confirmPick.teamName}</strong> need to win this fixture outright.
                                    If they draw or lose, you're eliminated. You won't be able to pick them again
                                    this season. This can't be undone once confirmed.
                                </>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    {confirmPick && (
                        <div className="flex items-center justify-center gap-3 py-3">
                            <Crest teamId={confirmPick.teamId} teamName={confirmPick.teamName} size={48} />
                            <span className="text-lg font-bold">{confirmPick.teamName}</span>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmPick(null)}>Cancel</Button>
                        <Button onClick={confirmSubmit} disabled={submitting}>
                            {submitting ? 'Locking in…' : 'Lock in pick'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
